import React, { createContext, useContext, useState, useCallback } from 'react'
import { ToastContainer, Toast } from '@/components/ui/Toast'

interface ToastContextType {
  showToast: (toast: Omit<Toast, 'id'>) => void
  hideToast: (id: string) => void
  clearAllToasts: () => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export function useToast() {
  const context = useContext(ToastContext)
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

interface ToastProviderProps {
  children: React.ReactNode
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center'
  maxToasts?: number
}

export function ToastProvider({ 
  children, 
  position = 'top-right',
  maxToasts = 5
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const showToast = useCallback((toastData: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9)
    const toast: Toast = {
      id,
      duration: 5000, // Default 5 seconds
      ...toastData,
    }

    setToasts(currentToasts => {
      const newToasts = [toast, ...currentToasts]
      // Limit the number of toasts
      return newToasts.slice(0, maxToasts)
    })
  }, [maxToasts])

  const hideToast = useCallback((id: string) => {
    setToasts(currentToasts => currentToasts.filter(toast => toast.id !== id))
  }, [])

  const clearAllToasts = useCallback(() => {
    setToasts([])
  }, [])

  const contextValue = {
    showToast,
    hideToast,
    clearAllToasts,
  }

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer
        toasts={toasts}
        onClose={hideToast}
        position={position}
      />
    </ToastContext.Provider>
  )
}

// Convenience hooks for different toast types
export function useToastHelpers() {
  const { showToast } = useToast()

  const showSuccess = useCallback((title: string, message?: string, action?: Toast['action']) => {
    showToast({ type: 'success', title, message, action })
  }, [showToast])

  const showError = useCallback((title: string, message?: string, action?: Toast['action']) => {
    showToast({ type: 'error', title, message, action, duration: 0 }) // Errors don't auto-dismiss
  }, [showToast])

  const showWarning = useCallback((title: string, message?: string, action?: Toast['action']) => {
    showToast({ type: 'warning', title, message, action })
  }, [showToast])

  const showInfo = useCallback((title: string, message?: string, action?: Toast['action']) => {
    showToast({ type: 'info', title, message, action })
  }, [showToast])

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
  }
}